import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Paperclip, ArrowLeft, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { formatTextWithLinks } from '../../../utils/formatters';
import LeaseEOIAttachmentsModal from '../../../components/LeaseEOIs/LeaseEOIAttachmentsModal';
import LeaseEOIFormModal from '../../../components/LeaseEOIs/LeaseEOIFormModal';
import type { LeaseEOI } from '../../../types/lease-eoi';

export default function LeaseEOIsAdmin() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedEoiId, setSelectedEoiId] = useState<number | null>(null);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedEOI, setSelectedEOI] = useState<LeaseEOI | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const { data: eois, isLoading } = useQuery({
    queryKey: ['admin', 'lease-eois'],
    queryFn: async () => {
      console.log('Fetching lease EOIs...'); // Debug log
      
      const { data, error } = await supabase
        .from('lease_eois')
        .select(`
          eoi_id,
          eoi_lot,
          eoi_date,
          eoi_doc,
          eoi_close_date
        `)
        .order('eoi_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Lease EOIs data:', data); // Debug log
      return data;
    }
  });

  const { data: attachments } = useQuery({
    queryKey: ['lease-eoi-attachments', selectedEoiId],
    queryFn: async () => {
      if (!selectedEoiId) return [];
      
      const { data, error } = await supabase
        .from('lease_eoi_attachments')
        .select('*')
        .eq('eoi_id', selectedEoiId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedEoiId
  });

  const handleAttachmentsClick = (eoiId: number) => {
    setSelectedEoiId(eoiId);
    setIsAttachmentsModalOpen(true);
  };

  const handleAttachmentsChange = () => {
    queryClient.invalidateQueries({ queryKey: ['lease-eoi-attachments', selectedEoiId] });
  };

  const handleSubmit = async (data: Omit<LeaseEOI, 'eoi_id'>) => {
    setError(null);
    setIsSubmitting(true);
    try {
      // Log the data being submitted
      console.log('Submitting EOI data:', data);

      if (selectedEOI) {
        // Update existing EOI
        const { error: updateError } = await supabase
          .from('lease_eois')
          .update(data)
          .eq('eoi_id', selectedEOI.eoi_id);
        
        if (updateError) {
          console.error('Update error details:', updateError);
          console.error('Data being updated:', data);
          throw updateError;
        }
      } else {
        // Add new EOI
        const { error: insertError } = await supabase
          .from('lease_eois')
          .insert(data);
        
        if (insertError) {
          console.error('Insert error details:', insertError);
          console.error('Data being inserted:', data);
          throw insertError;
        }
      }
      
      await queryClient.invalidateQueries({ queryKey: ['admin', 'lease-eois'] });
      setIsFormModalOpen(false);
      setSelectedEOI(undefined);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving the EOI');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEOIs = eois?.filter(eoi => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        eoi.eoi_lot?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lease EOIs</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage Lease Expressions of Interest
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedEOI(undefined);
              setIsFormModalOpen(true);
            }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Lease EOI
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search lease EOIs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Filter className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EOI ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EOI Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Close Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes and Docs
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredEOIs?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No lease EOIs found
                  </td>
                </tr>
              ) : (
                filteredEOIs?.map((eoi) => (
                  <tr key={eoi.eoi_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedEOI(eoi);
                          setIsFormModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {eoi.eoi_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {eoi.eoi_lot || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(eoi.eoi_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(eoi.eoi_close_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTextWithLinks(eoi.eoi_doc) || 'No notes'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      {selectedEoiId && (
        <LeaseEOIAttachmentsModal
          isOpen={isAttachmentsModalOpen}
          onClose={() => setIsAttachmentsModalOpen(false)}
          eoiId={selectedEoiId}
          attachments={attachments || []}
          onAttachmentsChange={handleAttachmentsChange}
        />
      )}

      <LeaseEOIFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedEOI(undefined);
          setError(null);
        }}
        initialData={selectedEOI}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedEOI ? 'Edit Lease EOI' : 'Add Lease EOI'}
        submitLabel={selectedEOI ? 'Save Changes' : 'Add EOI'}
      />
    </div>
  );
}
